// src/lib/toast.ts
import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
}

interface ToastStore {
  items: ToastItem[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION_MS = 3000;
const timers = new Map<string, number>();

function makeId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: (tone, message) => {
    const id = makeId();
    set((s) => ({ items: [...s.items, { id, tone, message }] }));
    const handle = window.setTimeout(() => {
      timers.delete(id);
      set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
    }, DEFAULT_DURATION_MS);
    timers.set(id, handle);
  },
  dismiss: (id) => {
    const handle = timers.get(id);
    if (handle != null) {
      clearTimeout(handle);
      timers.delete(id);
    }
    set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
  },
}));

export const toast = {
  success: (message: string) => useToastStore.getState().push('success', message),
  error: (message: string) => useToastStore.getState().push('error', message),
  info: (message: string) => useToastStore.getState().push('info', message),
};
